import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { chain, uniqBy } from "lodash";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Button, Divider, Form, Header, Segment } from "semantic-ui-react";
import { ControlledCheckbox } from "../../../../../../components/ControlledForm";
import { CollapsibleSection } from "../../../../../../components/global_components/CollapsibleSection";
import { Flyout } from "../../../../../../components/global_components/Flyout";
import PlaceholderUntilLoaded from "../../../../../../components/global_components/PlaceholderUntilLoaded";
import { ZonedDateTime } from "../../../../../../components/global_components/ZonedDateTime";
import Tooltip from "../../../../../../components/Tooltip";
import { GetNotifySubscriberContactMethodResponse, SubscriptionGroup } from "../../../../../../queries/notify";
import { useNotifySubscriberContactMethod } from "../../../../hooks/useNotifySubscriberContactMethod";
import { useNotifySubscriptionGroups } from "../../../../hooks/useNotifySubscriptionGroups";
import { useNotifySubscriptionPreferencesUpdate } from "../../../../hooks/useNotifySubscriptionPreferencesUpdate";
import { OptOutSubscriberModal } from "../modals/OptOutSubscriberModal";
import { NotifySubscribersInfiniteTableView } from "../NotifySubscribersInfiniteTable";
import { buildIdentifierString } from "../utils/buildIdentifierString";
import { OptInSubscriberModal } from "../modals/OptInSubscriberModal";
import { useNotifyBulkInviteCreate } from "../../../../hooks/useNotifyBulkInviteCreate";
import { ToastStatuses } from "../../../../../../components/global_components/Toast";
import { faLock, faUserMinus, faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import { OverflowTooltip } from "../../../../../global/components/tooltips/OverflowTooltip";
import { ContactAvatar } from "../../../../../permits/components/ContactAvatar";
import { RemoveSubscriberContactMethodModal } from "../modals/RemoveSubscriberContactMethodModal";
import { SubscriptionGroupSublabel } from "../../notifications/components/SubscriptionGroupSublabel";
import { iconLookup } from "../utils/iconLookup";
import { auditTrailRecord } from "./NotifyContactDetailFlyout";

export type HistoryCategory = GetNotifySubscriberContactMethodResponse["history"][0]["category"];
type FormType = {
  subscriptionGroups: { subscriptionGroupId: number; value: boolean }[]; //Used to determine whether you RECEIVE notifications or not
};

export function SubscriberDetailFlyout({
  view,
  subscriberId,
  onClose,
}: {
  view: NotifySubscribersInfiniteTableView;
  subscriberId?: number | null;
  onClose: () => void;
}) {
  const { addToast } = useToasts();
  const { site } = useParams<{ site: string }>();
  const { control, handleSubmit, setValue, getValues } = useForm<FormType>({
    defaultValues: {
      subscriptionGroups: [],
    },
  });

  const [shouldShowMoreSendHistory, setShouldShowMoreSendHistory] = useState(false);
  const [shouldShowMoreAuditTrail, setShouldShowMoreAuditTrail] = useState(false);

  const [isOptOutModalOpen, setIsOptOutModalOpen] = useState(false);
  const [isOptInModalOpen, setIsOptInModalOpen] = useState(false);

  const [isSubscriberContactMethodRemovalModalOpen, setIsSubscriberContactMethodRemovalModalOpen] = useState(false);
  const [selectedSubscriptionGroupToRemove, setSelectedSubscriptionGroupToRemove] = useState<SubscriptionGroup | null>(
    null,
  );

  // Manual loading state management since the save buttons use the same hooks
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isSaveAndResendLoading, setIsSaveAndResendLoading] = useState(false);

  const { subscriberContactMethod, isSubscriberContactMethodLoading } = useNotifySubscriberContactMethod({
    id: subscriberId,
  });

  const { subscriptionGroups, isSubscriptionGroupsLoading } = useNotifySubscriptionGroups({});

  // Setup default values for the form based on the subscriber's current preferences
  useEffect(() => {
    if (!subscriberContactMethod || !subscriptionGroups) return;

    const formValues = getValues();

    const formIndexByGroupId = formValues.subscriptionGroups
      ? new Map(formValues.subscriptionGroups.map((value, index) => [value.subscriptionGroupId, index]))
      : new Map();

    const preferences = subscriberContactMethod.subscriptionPreferences;

    // iterate through the subscription groups
    subscriptionGroups?.forEach((group) => {
      const preference = preferences.find((pref) => pref.subscriptionGroupId === group.id);
      const isSubscribed = preference ? preference.type === "Subscribe" : false;

      const formIndex = formIndexByGroupId.get(group.id);
      if (group.autoSubscribe) {
        // If the contact method has an unsubscribe preference for that group, set checkbox value to false, if not, set it to true
        if (formIndex !== undefined) {
          setValue(`subscriptionGroups.${formIndex}`, {
            subscriptionGroupId: group.id,
            value: preference ? preference.type !== "Unsubscribe" : true,
          });
        }
      } else {
        if (formIndex !== undefined) {
          setValue(`subscriptionGroups.${formIndex}`, {
            subscriptionGroupId: group.id,
            value: isSubscribed,
          });
        }
      }
    });
  }, [subscriberContactMethod]);

  const { editSubscriptionPreferences } = useNotifySubscriptionPreferencesUpdate({
    onSuccess: async () => {
      // its bulk so they should all be same
      addToast(`Subscription preferences updated`, {
        appearance: ToastStatuses.Success,
        autoDismiss: true,
      });
    },
  });

  const { sendBulkInvite } = useNotifyBulkInviteCreate({
    onError: () => {
      addToast("Failed to send invitations", { appearance: ToastStatuses.Error, autoDismiss: true });
    },
  });

  const subscriptionGroupIdToNameMap: Record<number, string> = subscriptionGroups
    ? subscriptionGroups.reduce(
        (acc, group) => {
          acc[group.id] = group.name;
          return acc;
        },
        {} as Record<number, string>,
      )
    : {};

  const buildAuditTrailCategoryString = (history: auditTrailRecord) => {
    switch (history.category) {
      case "Invite":
        return "Invited";
      case "OptIn":
        return "Opted In";
      case "OptOut":
        return "Opted Out";
      case "Verify":
        return "Verified";
      case "Create":
        return "Created";
      case "Update":
        return "Updated";
      case "AddToGroup":
        return `Added to ${
          Array.isArray(history.details?.subscriptionGroupIds)
            ? history.details.subscriptionGroupIds
                .map((subscriptionGroupId: number) => subscriptionGroupIdToNameMap[subscriptionGroupId] || "Unknown")
                .join(", ")
            : "No subscription groups"
        }`;
      case "RemoveFromGroup":
        return `Removed from ${subscriptionGroupIdToNameMap[history.details?.subscriptionGroupId]}`;
      case "SubscriptionGroupPreferenceUpdate":
        const hasPreferenceChanges = history.details?.preferenceChanges;
        const updatedPreference = history.details.preferenceChanges[0].next;
        if (!updatedPreference || !hasPreferenceChanges) return "no updated preferences found";
        return `${updatedPreference.type}`;
      default:
        return history.category;
    }
  };

  const savePreferences: SubmitHandler<FormType> = async (data) => {
    if (!subscriberContactMethod) return;

    const getCurrentSubscriptionGroupIds = (groups: { id: number; autoSubscribe: boolean }[]) => {
      return groups.map((group) => {
        const subscriberPreference = subscriberContactMethod.subscriptionPreferences.find(
          (preference) => preference.subscriptionGroupId === group.id,
        );
        if (!subscriberPreference) {
          return {
            subscriptionGroupId: group.id,
            value: group.autoSubscribe, // No preference exists, use default
          };
        }
        return {
          subscriptionGroupId: group.id,
          value: subscriberPreference?.type === "Subscribe",
        };
      });
    };

    const currentSubscriptionGroupIds = getCurrentSubscriptionGroupIds(subscriptionGroups || []);

    const [groupsToSubscribe, groupsToUnsubscribe] = chain(data.subscriptionGroups)
      .zip(currentSubscriptionGroupIds)
      .reduce(
        (acc, [group, currentGroup]) => {
          if (!group || !currentGroup || group.value === currentGroup.value) return acc;
          return [...acc, group];
        },
        [] as { subscriptionGroupId: number; value: boolean }[],
      )
      .partition((group) => group.value)
      .value();

    if (groupsToSubscribe.length > 0) {
      await editSubscriptionPreferences({
        subscriberContactMethodIds: [subscriberContactMethod.id],
        subscriptionGroupIds: groupsToSubscribe.map((group) => group.subscriptionGroupId),
        type: "Subscribe",
        overwriteExistingPreference: true,
      });
    }
    if (groupsToUnsubscribe.length > 0) {
      await editSubscriptionPreferences({
        subscriberContactMethodIds: [subscriberContactMethod.id],
        subscriptionGroupIds: groupsToUnsubscribe.map((group) => group.subscriptionGroupId),
        type: "Unsubscribe",
        overwriteExistingPreference: true,
      });
    }
  };

  const flyoutHeaderRef = useRef<HTMLDivElement | null>(null);

  const notificationSendHistory = uniqBy(
    subscriberContactMethod?.communicationAttempts.filter(
      (communicationAttempt) => !!communicationAttempt.notification,
    ) ?? [],
    (communicationAttempt) => {
      return communicationAttempt.notification?.id;
    },
  );

  const onSave = async (data: FormType) => {
    setIsSaveLoading(true);
    await savePreferences(data);
    setIsSaveLoading(false);
    addToast("Subscription preferences saved", { appearance: ToastStatuses.Success, autoDismiss: true });
    onClose();
  };

  const onSaveAndResend = async (data: FormType) => {
    if (!subscriberContactMethod) return;

    setIsSaveAndResendLoading(true);
    await savePreferences(data);
    await sendBulkInvite({
      recipients: [
        {
          identifier: subscriberContactMethod.identifier,
          type: subscriberContactMethod.type as "Email" | "Phone",
        },
      ],
      options: {
        autoEnroll: null,
        resendInvitations: true,
        resendOptedOut: true,
      },
      subscriptionPreferences: [],
    });
    setIsSaveAndResendLoading(false);
    addToast("Subscription preferences saved and invitation sent", {
      appearance: ToastStatuses.Success,
      autoDismiss: true,
    });
    onClose();
  };

  const subscriberIsOptedOut = subscriberContactMethod?.status === "OptedOut";

  const handleOpenSubscriberRemovalModal = (group: SubscriptionGroup) => {
    setSelectedSubscriptionGroupToRemove(group);
    setIsSubscriberContactMethodRemovalModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedSubscriptionGroupToRemove(null);
    setIsSubscriberContactMethodRemovalModalOpen(false);
  };

  return (
    <>
      <OptOutSubscriberModal
        subscriberContactMethodId={subscriberContactMethod?.id}
        close={() => setIsOptOutModalOpen(false)}
        isOpen={isOptOutModalOpen}
        onSubmit={onClose}
      />
      <OptInSubscriberModal
        subscriberContactMethodId={subscriberContactMethod?.id}
        close={() => setIsOptInModalOpen(false)}
        isOpen={isOptInModalOpen}
        onSubmit={onClose}
      />
      <RemoveSubscriberContactMethodModal
        subscriberContactMethodId={subscriberContactMethod?.id}
        group={selectedSubscriptionGroupToRemove}
        close={() => setIsSubscriberContactMethodRemovalModalOpen(false)}
        isOpen={isSubscriberContactMethodRemovalModalOpen}
        onSubmit={handleCloseModal}
      />

      <Flyout open={!!subscriberId} onClose={onClose} disableClose={isOptOutModalOpen || isOptInModalOpen}>
        <Flyout.Header>
          <PlaceholderUntilLoaded
            isLoading={isSubscriberContactMethodLoading || isSubscriptionGroupsLoading}
            lineCount={1}
          >
            <div className='tw-flex tw-w-full tw-items-center tw-justify-between ' ref={flyoutHeaderRef}>
              <Header className='tw-mb-0 tw-mt-[2px] tw-flex tw-max-w-[50%]  tw-items-center tw-gap-1'>
                {iconLookup[subscriberContactMethod?.type ?? ""]?.()}
                <OverflowTooltip
                  wrapperClassName='tw-w-full tw-overflow-hidden tw-text-ellipsis'
                  mountNode={flyoutHeaderRef.current}
                >
                  <span>{buildIdentifierString(subscriberContactMethod)}</span>
                </OverflowTooltip>
              </Header>

              <div className='tw-flex tw-items-center '>
                <Button basic className='tw-m-0 tw-flex tw-gap-2'>
                  Send Test
                  <FontAwesomeIcon icon={faLock} className='tw-text-red' />
                </Button>
                {view === "Enrolled" && (
                  <Button
                    className='button-ghost negative-bright'
                    content='Opt-out'
                    onClick={() => setIsOptOutModalOpen(true)}
                  />
                )}
                {(view === "Invited" || view === "OptedOut") && (
                  <Button
                    className='button-ghost negative-bright'
                    content='Opt-in'
                    onClick={() => setIsOptInModalOpen(true)}
                  />
                )}
              </div>
            </div>
          </PlaceholderUntilLoaded>
        </Flyout.Header>
        {subscriberIsOptedOut && (
          <Segment className='tw-mx-gg-4 tw-rounded-sm tw-border-none tw-bg-error-95 tw-px-gg-3 tw-py-gg-2 tw-text-[14px] tw-font-semibold '>
            This subscriber has opted out
          </Segment>
        )}
        <Flyout.Body key={subscriberContactMethod?.identifier}>
          <PlaceholderUntilLoaded
            isLoading={isSubscriberContactMethodLoading || isSubscriptionGroupsLoading}
            lineCount={2}
          >
            {subscriberContactMethod?.type === "App" && (
              <div className='tw-mb-3 tw-flex tw-flex-row tw-gap-x-8'>
                <div>
                  <strong>App Version</strong>
                  <p>{subscriberContactMethod.metadata.appVersion}</p>
                </div>

                <div>
                  <strong>Location</strong>
                  <p>{subscriberContactMethod.metadata.deviceLocation ?? "No Location Found"}</p>
                </div>
                <div>
                  <strong>Manufacturer</strong>
                  <p>{subscriberContactMethod.metadata.deviceType === "ios" ? "Apple" : "Android"}</p>
                </div>
              </div>
            )}
            {subscriberContactMethod?.potentialPersonMatches && (
              <div className='tw-mb-gg-4 tw-flex tw-items-center  tw-gap-2'>
                {subscriberContactMethod?.potentialPersonMatches.slice(0, 2).map((person) => {
                  const contact = {
                    firstName: person.firstName,
                    lastName: person.lastName,
                    contactType: null,
                    userId: person.id,
                  };
                  return (
                    <div className='tw-flex tw-items-center tw-gap-2' key={person.id}>
                      <ContactAvatar
                        linkToConnectedCitizen={true}
                        contact={contact}
                        size='40px'
                        subscriberContactMethodId={subscriberContactMethod.id}
                        onClose={onClose}
                      />
                      <div className='tw-flex tw-flex-col'>
                        <span className='tw-tg-label-medium-prominent tw-truncate'>{person.fullName}</span>
                        <span className='tw-truncate tw-text-[12px] tw-font-semibold tw-text-primary-hint'>
                          Potential Contact Match
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Subscription Group/Preferences */}
            {view !== "OptedOut" && (
              <div className='tw-mb-gg-6'>
                {subscriptionGroups?.map((group, index) => (
                  <Form.Field
                    key={group.id}
                    className='tw-mx-0 tw-my-1 tw-flex tw-items-center tw-justify-between tw-gap-2'
                  >
                    <div className='tw-flex tw-items-center tw-gap-2'>
                      <ControlledCheckbox
                        control={control}
                        name={`subscriptionGroups.${index}`}
                        className='tw-m-0'
                        label={group.name}
                        transform={{
                          input: (value) => value?.value ?? false,
                          output: (value) => ({ subscriptionGroupId: group.id, value }),
                        }}
                        defaultValue={{
                          subscriptionGroupId: group.id,
                          value: false,
                        }}
                      />
                      <SubscriptionGroupSublabel group={group} />
                    </div>
                    {group.visibility !== "Public" &&
                      (() => {
                        const isMember = subscriberContactMethod?.subscriptionPreferences.some(
                          (pref) => pref.subscriptionGroupId === group.id,
                        );
                        return (
                          <FontAwesomeIcon
                            icon={isMember ? faUserMinus : faUserPlus}
                            className={`tw-cursor-pointer ${isMember ? "tw-text-red" : "tw-text-primary-hint"}`}
                            title={isMember ? "Remove from Group" : "Add to Group"}
                            onClick={() =>
                              isMember
                                ? handleOpenSubscriberRemovalModal(group)
                                : editSubscriptionPreferences({
                                    subscriberContactMethodIds: subscriberContactMethod
                                      ? [subscriberContactMethod?.id]
                                      : [],
                                    subscriptionGroupIds: [group.id],
                                    type: "Subscribe",
                                    overwriteExistingPreference: true,
                                    isAddingToGroup: true,
                                  })
                            }
                          />
                        );
                      })()}
                  </Form.Field>
                ))}
              </div>
            )}
            <Divider />
            <CollapsibleSection title='Audit Trail'>
              {!subscriberContactMethod?.history.length && (
                <div className='tw-text-[12px] tw-text-black-light'>No audit trail records</div>
              )}
              {subscriberContactMethod?.history
                .slice(0, shouldShowMoreAuditTrail ? subscriberContactMethod?.history.length : 5)
                .map((history) => {
                  const details = history.details ?? {};
                  const hasAppVersionChange = history.category === "Update" && details.appVersionChange;
                  const hasFCMTokenChange = history.category === "Update" && details.fcmTokenChange;
                  const hasJustification = history.category === "OptIn" && !!details.justification;
                  return (
                    <div key={history.id}>
                      <div className='tw-mt-1 tw-flex tw-justify-between'>
                        <span className='tw-text-[12px] tw-leading-4'>
                          {buildAuditTrailCategoryString(history)}
                          <Tooltip>{history.event}</Tooltip>
                        </span>
                        <span className='tw-whitespace-nowrap tw-text-[12px] tw-leading-4 tw-text-black-light'>
                          <ZonedDateTime value={history.createdAt} formatString='MMM d, yyyy' />
                        </span>
                      </div>

                      {hasAppVersionChange && (
                        <div className='tw-mb-1 tw-text-[12px] tw-leading-4 tw-text-black-light'>
                          App Version Changed:{" "}
                          {details.appVersionChange.from
                            ? `${details.appVersionChange.from} → ${details.appVersionChange.to}`
                            : details.appVersionChange.to}
                        </div>
                      )}
                      {hasFCMTokenChange && (
                        <div className='tw-mb-1  tw-text-[12px] tw-leading-4 tw-text-black-light'>
                          FCM Token Has been Updated!
                        </div>
                      )}
                      {hasJustification && (
                        <div className='tw-mb-1 tw-text-[12px] tw-leading-4 tw-text-black-light'>
                          {(history.details ?? {}).justification}
                        </div>
                      )}
                    </div>
                  );
                })}
              {(subscriberContactMethod?.history.length ?? 0) > 5 && (
                <Button
                  className='button-ghost'
                  size='tiny'
                  content={shouldShowMoreAuditTrail ? "Show Less" : "Show More"}
                  onClick={() => setShouldShowMoreAuditTrail((prev) => !prev)}
                />
              )}
            </CollapsibleSection>
            <CollapsibleSection title='Send History'>
              {!notificationSendHistory.length && <div className='tw-text-black-light'>No send history records</div>}
              {notificationSendHistory
                .slice(0, shouldShowMoreSendHistory ? notificationSendHistory.length : 5)
                .map((communicationAttempt) => {
                  if (!communicationAttempt.notification) return null;
                  return (
                    <div key={communicationAttempt.id}>
                      <div className='tw-mt-1 tw-flex tw-justify-between'>
                        <a
                          href={`/${site}/communications/edit/${communicationAttempt.notification.id}`}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          {communicationAttempt.notification.headline}
                        </a>
                        <span className='tw-whitespace-nowrap tw-text-black-light'>
                          {/* Dec 17, 2025 */}
                          <ZonedDateTime value={communicationAttempt.createdAt} formatString='MMM d, yyyy' />
                        </span>
                      </div>
                      <Divider className='tw-my-1' />
                    </div>
                  );
                })}
              {(notificationSendHistory.length ?? 0) > 5 && (
                <Button
                  className='button-ghost'
                  size='tiny'
                  content={shouldShowMoreSendHistory ? "Show Less" : "Show More"}
                  onClick={() => setShouldShowMoreSendHistory((prev) => !prev)}
                />
              )}
            </CollapsibleSection>
          </PlaceholderUntilLoaded>
          <PlaceholderUntilLoaded
            isLoading={isSubscriberContactMethodLoading || isSubscriptionGroupsLoading}
            lineCount={4}
          />
        </Flyout.Body>
        <Flyout.Footer>
          <div className='tw-flex tw-justify-between'>
            <Button className='button-ghost' content='Close' onClick={onClose} />
            <div className='tw-flex tw-gap-3'>
              {!!subscriberContactMethod &&
                subscriberContactMethod?.type !== "App" &&
                subscriberContactMethod?.status !== "Enrolled" && (
                  <Button
                    className='tw-m-0'
                    secondary
                    content='Save & Resend Invite'
                    onClick={handleSubmit(onSaveAndResend)}
                    disabled={isSaveAndResendLoading}
                    loading={isSaveAndResendLoading}
                  />
                )}
              <Button
                className='tw-m-0'
                primary
                content='Save'
                onClick={handleSubmit(onSave)}
                disabled={isSaveLoading}
                loading={isSaveLoading}
              />
            </div>
          </div>
        </Flyout.Footer>
      </Flyout>
    </>
  );
}
